// eslint-disable-next-line no-restricted-imports
import store from '@/vuex/store';
import { USER_ROLES } from '@/constants';
import { $canOrg, $can, $canSub } from '@pendo/permissions';
import { fetchHasOnboardingAccess } from '@/onboarding-homepage/helpers/onboarding-access/onboarding-access.helper';
import useOnboarding from '@/onboarding-homepage/use/use-onboarding';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';
import { hasManagementAccessForAppIds, hasManagementAccessForAppId } from '@/admin/admin.utils';

export const SUBSCRIPTION_PLANS = {
    PENDO_FREE: 'pendoFree',
    PENDO_STARTER: 'pendoStarter',
    PENDO_STARTER_2024: 'pendoStarter2024',
    PENDO_FREE_TRIAL: 'pendoFreeTrial',
};

export const ADDITIONAL_ROLES = {
    ORG_ADMIN: 'ORG_ADMIN',
    OPS: 'OPS',
    PENDOZER: 'pendozer',
    ADOPT_MANAGER: 'adoptManager',
};

// Check if user has correct feature flag
export function hasFeatureFlag (featureFlag) {
    return store.getters['subscription/getFeatureFlag'](featureFlag);
}

export function canSubmitFeedback () {
    return store.getters['vocItems/canSubmitFeedback'];
}

export function hasSegmentFlag (segmentFlag) {
    return store.getters['auth/hasSegmentFlag'](segmentFlag);
}

export function hasProductLine (productLine, metadataName) {
    return store.getters['productLines/hasProductLinePurchased'](productLine, metadataName);
}

export function hasPortfolioSummaryDashboard () {
    return store.getters['dashboards/hasPortfolioSummaryDashboard'];
}

export function isScimEnabled () {
    return store.getters['subscription/isScimEnabled'];
}

export function hasUserPermissions (permissionsLevel) {
    switch (permissionsLevel) {
        case USER_ROLES.ADMIN:
            return store.getters['auth/hasAdminAccess'];
        case ADDITIONAL_ROLES.OPS:
            return store.getters['auth/isOps'];
        case ADDITIONAL_ROLES.ORG_ADMIN:
            return store.getters['auth/isAuthorizedForRoute']('ORG_ADMIN');
        case USER_ROLES.SUPER:
            return store.getters['auth/hasSuperAccess'] || store.getters['auth/isOps'];
        case ADDITIONAL_ROLES.PENDOZER:
            return store.getters['auth/isPendozer'];
        case ADDITIONAL_ROLES.ADOPT_MANAGER:
            return store.getters['auth/isAdoptManager'];
    }
}

export function isAdoptOnCore () {
    return store.getters['subscription/isAdoptOnCore'];
}

export function isDigitalAdoptionOnly () {
    return store.getters['subscription/isDigitalAdoptionOnly'];
}

export function isPartnerAnalyticsSub () {
    return store.getters['subscription/isPartnerAnalytics'];
}

export function isRouteDisabledForSubscriptionPlan (disabledSubscriptionPlans) {
    return disabledSubscriptionPlans.some((disabledSubscriptionPlan) => {
        switch (disabledSubscriptionPlan) {
            case SUBSCRIPTION_PLANS.PENDO_FREE:
                return store.getters['subscription/isPendoFree'];
            case SUBSCRIPTION_PLANS.PENDO_FREE_TRIAL:
                return store.getters['subscription/isInTrial'];
            case SUBSCRIPTION_PLANS.PENDO_STARTER:
                return store.getters['subscription/isStarterPackage'];
            case SUBSCRIPTION_PLANS.PENDO_STARTER_2024:
                return store.getters['subscription/isStarter2024Package'];
            default:
                return false;
        }
    });
}

export function hasOrgTabsPermissions (tabName) {
    const hasOrgSettingsAccess = $canOrg().entity('organizationSetting').action('view');

    const hasOrgResourcesAccess = $canOrg().entity('organizationResources').action('edit');

    const { organizationFlags } = store.getters['organization/getActive'];

    if (!organizationFlags) {
        return false;
    }

    if (tabName !== 'mau' && (hasUserPermissions(USER_ROLES.SUPER) || hasUserPermissions(ADDITIONAL_ROLES.OPS))) {
        return true;
    }

    switch (tabName) {
        case 'mau':
            return (
                hasSegmentFlag('orgMauVisible') &&
                organizationFlags.enableMauView &&
                (hasUserPermissions(USER_ROLES.SUPER) ||
                    hasUserPermissions(ADDITIONAL_ROLES.OPS) ||
                    hasOrgSettingsAccess)
            );
        case 'orgLibrary':
            return (
                hasOrgResourcesAccess &&
                organizationFlags.enableResourceSharing &&
                hasProductLine('guides', 'copyThemeEnabled')
            );
        case 'users':
            return hasOrgSettingsAccess;
        case 'sso':
            return hasOrgSettingsAccess && (organizationFlags.enableSaml || isScimEnabled());
        case 'domainVerification':
            return hasOrgSettingsAccess && hasSegmentFlag('domainVerification');
        case 'cname':
            return hasOrgSettingsAccess && organizationFlags.enableCname;
        case 'planDetails':
            return hasOrgSettingsAccess && organizationFlags.enablePlanDetails;
        case 'essentialContacts':
            return hasOrgSettingsAccess && hasSegmentFlag('essentialContacts');
        default:
            return false;
    }
}

export function beforeEnterGuardRedirectByAfter (to) {
    if (!store.getters['auth/isAuthenticated']) return;

    if (to.query.after) {
        const afterUri = decodeURIComponent(to.query.after);
        // Hi, this check is important, specifically that we're looking for `/in-app/`
        // at the START of the uri - outside of needing to make sure the user
        // wants to go to the classic designer under normal usage, we also need to
        // ensure a malicious actor can't set `after` to `some-bad-domain.com` to force
        // a redirect.
        if (afterUri.indexOf('/in-app/') === 0) {
            window.location.href = afterUri;
        }
    }
}

export function buildUnauthenticatedLoginRedirectRoute () {
    const searchParams = new URLSearchParams(window.location.search);
    const afterParam = searchParams.get('after');

    const query = {};

    if (afterParam) {
        query.after = encodeURIComponent(afterParam);
    } else {
        const pathQuery = String(window.location.href).replace(window.location.origin, '');
        query.after = encodeURIComponent(pathQuery);
    }

    return {
        to: 'Login',
        query,
    };
}

function adminRouteSubscriptionPostChecks ({ isAdoptOnCoreAllowed = false } = {}) {
    const onSampleData = store.getters['subscription/getFeatureFlag']('sampleDataSubscription');
    const preSampleDataSubId = store.getters['auth/preSampleDataSubId'];

    if (onSampleData && preSampleDataSubId !== null) {
        return { name: 'Home' };
    }

    const subIsAdoptOnCore = store.getters['subscription/isAdoptOnCore'];

    if (!isAdoptOnCoreAllowed && subIsAdoptOnCore && !hasUserPermissions(ADDITIONAL_ROLES.OPS)) {
        return { name: 'NoAccess' };
    }

    return null;
}

export function adminRoutesGuard ({ isAdoptOnCoreAllowed = false } = {}) {
    if (!hasUserPermissions(USER_ROLES.ADMIN)) return { name: 'NoAccess' };

    return adminRouteSubscriptionPostChecks({ isAdoptOnCoreAllowed });
}

export function appAdminRouteGuard (to) {
    const appId = to?.params?.appId;

    if (!appId) return { name: 'NoAccess' };

    const isAdmin = hasUserPermissions(USER_ROLES.ADMIN);

    const hasManagementAccess = hasManagementAccessForAppId(appId);

    if (!isAdmin && !hasManagementAccess) {
        return { name: 'NoAccess' };
    }

    if (isAdmin) {
        return adminRoutesGuard();
    }

    return adminRouteSubscriptionPostChecks();
}

export function hasResourceCenterAccess () {
    const actions = ['create', 'edit', 'publish', 'remove'];

    const canView = store.getters['apps/list'].some((app) => {
        return actions.some((action) => {
            return $can(app.id).entity('resourceCenter').action(action).source('ANY').target('ANY');
        });
    });

    return canView && hasFeatureFlag('enableP2Access');
}

export function hasGuideThemesAccess () {
    const hasGuidesProductLine = hasProductLine('guides');
    const isAdmin = hasUserPermissions(USER_ROLES.ADMIN);
    const actions = ['create', 'edit', 'remove'];
    const hasGuideThemeAccess = actions.some((action) => {
        return $canSub().entity('guideTheme').action(action);
    });

    return hasGuidesProductLine && (isAdmin || hasGuideThemeAccess);
}

export function canAccessDomainSettings () {
    const hasOrchestrateEmailDomain = hasSegmentFlag('emailDomainSetup') && hasProductLine('orchestrate');

    if (hasOrchestrateEmailDomain) {
        return true;
    }

    const hasVocEmailDomain = hasProductLine('voc', 'email') && hasFeatureFlag('vocEmail');

    return !!hasVocEmailDomain;
}

export function hasSessionReplayViewerAccess () {
    return store.getters['auth/hasSessionReplayViewerAccess']();
}

export function hasApplicationManagementAccess () {
    return hasManagementAccessForAppIds(store.getters['apps/list'].map(({ id }) => id));
}

export function canEditSomePage () {
    const webList = store.getters['apps/webList'];
    const extensionList = store.getters['apps/extensionList'];
    const allApps = [...webList, ...extensionList];

    return allApps.some((app) => {
        return $can(app.id).entity('page').action('edit');
    });
}

export function canEditPagesForAppId (appId) {
    return $can(appId).entity('page').action('edit');
}

export async function homeBeforeEnter (to) {
    const isNewUser = !store.state.auth.user.useCase && !store.state.auth.user.jobRole;
    const query = isNewUser ? { ...to.query, welcome: true } : { ...to.query };
    const { params } = to;

    const isAEUSub = hasProductLine(PRODUCT_LINES.ADOPT);

    const { hasOnboardingAccess, setHasOnboardingAccess } = useOnboarding();

    if (hasOnboardingAccess.value === null && !isAEUSub) {
        const canAccess = await fetchHasOnboardingAccess();
        setHasOnboardingAccess(canAccess);
    }

    if (hasOnboardingAccess.value && !isAEUSub) {
        return { name: 'OnboardingHomepage', params, query };
    }

    if (hasSegmentFlag('agentHomepage')) {
        return { name: 'Homepage', params, query };
    }

    const homeDashboardId = store.getters['userSettings/settingByName']('homeDashboardId', true)?.value;
    if (homeDashboardId) {
        return { name: 'Dashboard', params: { ...params, id: homeDashboardId }, query };
    }

    if (!hasProductLine(PRODUCT_LINES.CORE, 'homepageEnabled') || isAEUSub) {
        if (isNewUser && !isAEUSub) {
            return { name: 'PostRegisterSteps', params, query };
        }

        return { name: 'DefaultDashboard', params, query };
    }

    return { name: 'Homepage', params, query };
}
